<template>
	<v-form ref="form" v-model="formValid">
		<v-container
			fluid
			class="d-flex justify-space-between form-page-gutter shipment-form-page flex-wrap flex-md-nowrap flex-lg-nowrap">
			<v-card elevation="0" min-width="60%" class="rounded-xl">
				<v-card-title
					v-if="!isUpdating"
					class="d-flex align-center justify-space-between flex-wrap flex-md-nowrap flex-lg-nowrap">
					<div>New Shipment</div>
					<v-btn
						color="primary"
						depressed
						rounded
						@click="addShipment"
						:loading="isLoading"
						:disabled="!formValid">
						<v-icon small class="mr-2">mdi-package-variant-closed</v-icon>
						Add New Shipment
					</v-btn>
				</v-card-title>
				<v-card-title v-else>
					<v-container
						class="px-0 d-flex align-center justify-space-between flex-wrap flex-md-nowrap flex-lg-nowrap">
						<div>Shipment Detail</div>
						<h3 class="primary--text font-italic font-weight-thin">
							<span>#{{ shipment.id }}</span>
							<span class="ml-2">
								{{ convertToDate(shipment.dateCreated) }}
							</span>
						</h3>
					</v-container>
					<v-container
						class="pa-0 d-flex justify-center justify-md-space-between justify-lg-space-between flex-wrap flex-md-nowrap flex-lg-nowrap"
						style="gap: 5px">
						<v-btn
							v-if="isUpdating && shipment.invoiceId"
							color="primary"
							depressed
							rounded
							@click="
								$router
									.push({
										name: 'InvoiceUpdate',
										params: { id: shipment.invoiceId },
									})
									.catch(() => {})
							">
							<v-icon small class="mr-2">mdi-file-document</v-icon>
							Go To Invoice
						</v-btn>
						<v-btn
							v-if="isUpdating && shipment.invoiceId"
							color="warning"
							depressed
							rounded
							:loading="isUpdateLoading"
							@click="updateDialog = !updateDialog">
							<v-icon small class="mr-2">mdi-upload</v-icon>
							Push to Invoice
						</v-btn>
						<v-btn
							v-if="isUpdating && !shipment.invoiceId"
							color="warning"
							depressed
							rounded
							@click="generateInvoice"
							:loading="isGenerateLoading"
							:disabled="!!shipment.invoiceId">
							<v-icon small class="mr-2">mdi-format-list-bulleted</v-icon>
							Generate Invoice
						</v-btn>

						<v-btn
							v-if="isUpdating"
							color="warning"
							depressed
							rounded
							@click="updateShipment"
							:loading="isLoading"
							:disabled="!formValid">
							<v-icon small class="mr-2">mdi-package-variant-closed</v-icon>
							Update Shipment
						</v-btn>

						<v-tooltip
							bottom
							v-if="isUpdating && !!shipment.invoiceId">
							<template v-slot:activator="{ on, attrs }">
								<div v-on="on" v-bind="attrs">
									<v-btn
										v-if="isUpdating"
										rounded
										depressed
										color="error"
										:disabled="!!shipment.invoiceId">
										<v-icon>mdi-delete</v-icon>
									</v-btn>
								</div>
							</template>
							<span>Can't delete. Invoice has been created</span>
						</v-tooltip>
						<v-btn
							v-if="isUpdating && !shipment.invoiceId"
							rounded
							depressed
							color="error"
							@click="deleteDialog = true"
							:loading="isDeleteLoading">
							<v-icon>mdi-delete</v-icon>
						</v-btn>
					</v-container>
				</v-card-title>

				<v-card-text class="py-0 mb-2">
					<v-container
						class="d-flex flex-wrap flex-md-nowrap flex-lg-nowrap"
						style="gap: 5px">
						<v-text-field
							v-model="shipment.mtlTracking"
							label="MTL Tracking #"
							dense
							rounded
							outlined></v-text-field>
						<v-autocomplete
							:rules="[
								(v) => !!v || 'Shipment Group is required',
							]"
							rounded
							outlined
							dense
							v-model="shipment.groupId"
							item-value="id"
							:item-text="formatShipmentGroup"
							label="Shipment Group"
							:items="shipmentGroups"
							required
							@change="handleGroupChange"></v-autocomplete>
						<v-autocomplete
							rounded
							outlined
							dense
							v-model="shipment.shipmentStatusId"
							label="Shipment Status"
							:items="shipmentStatuses"
							item-value="id"
							item-text="displayName"
							:disabled="!isUpdating"></v-autocomplete>
					</v-container>
				</v-card-text>
				<v-card-text class="py-0 mb-2">
					<v-container
						class="d-flex py-0 my-0 flex-wrap flex-md-nowrap flex-md-nowrap">
						<v-container>
							<v-autocomplete
								:rules="[(v) => !!v || 'Sender is required']"
								:items="customers"
								:item-text="getCustomerName"
								item-value="id"
								label="Sender"
								dense
								rounded
								outlined
								v-model="shipment.senderId"
								@change="updateSenderInfo">
								<template v-slot:no-data>
									<v-card elevation="0">
										<v-card-text class="text-center">
											<h4 class="mb-2">
												Sender not found
											</h4>
											<v-btn
												depressed
												small
												rounded
												color="primary"
												@click="
													customerDialog =
														!customerDialog
												">
												<v-icon small class="mr-2">mdi-plus</v-icon>
												Add Sender
											</v-btn>
										</v-card-text>
									</v-card>
								</template>
							</v-autocomplete>
							<v-text-field
								:rules="[(v) => !!v || 'Address is required']"
								label="Address Line 1"
								dense
								rounded
								outlined
								v-model="
									shipment.senderAddress1
								"></v-text-field>
							<v-text-field
								label="Address Line 2"
								dense
								rounded
								outlined
								v-model="
									shipment.senderAddress2
								"></v-text-field>
							<v-text-field
								:rules="[(v) => !!v || 'Phone is required']"
								v-model="shipment.senderPhone"
								label="Phone"
								dense
								rounded
								outlined></v-text-field>
							<v-text-field
								v-model="shipment.senderEmail"
								label="Email"
								dense
								rounded
								outlined></v-text-field>
						</v-container>
						<v-container>
							<v-autocomplete
								:rules="[(v) => !!v || 'Receiver is required']"
								:items="customers"
								:item-text="getCustomerName"
								item-value="id"
								label="Receiver"
								dense
								rounded
								outlined
								v-model="shipment.receiverId"
								@change="updateReceiverInfo">
								<template v-slot:no-data>
									<v-card elevation="0">
										<v-card-text class="text-center">
											<h4 class="mb-2">
												Receiver not found
											</h4>
											<v-btn
												depressed
												small
												rounded
												color="primary"
												@click="
													customerDialog =
														!customerDialog
												">
												<v-icon small class="mr-2">mdi-plus</v-icon>
												Add Receiver
											</v-btn>
										</v-card-text>
									</v-card>
								</template>
							</v-autocomplete>
							<v-text-field
								:rules="[(v) => !!v || 'Address is required']"
								label="Address Line 1"
								dense
								rounded
								outlined
								v-model="
									shipment.receiverAddress1
								"></v-text-field>
							<v-text-field
								label="Address Line 2"
								dense
								rounded
								outlined
								v-model="
									shipment.receiverAddress2
								"></v-text-field>
							<v-text-field
								:rules="[(v) => !!v || 'Phone is required']"
								v-model="shipment.receiverPhone"
								label="Phone"
								dense
								rounded
								outlined></v-text-field>
							<v-text-field
								v-model="shipment.receiverEmail"
								label="Email"
								dense
								rounded
								outlined></v-text-field>
						</v-container>
					</v-container>
				</v-card-text>
				<v-card-text class="py-0 mb-2" v-if="isAirShipment">
					<v-container
						class="d-flex justify-space-between flex-wrap flex-md-nowrap flex-lg-nowrap"
						style="gap: 5px">
						<v-text-field
							:rules="numberRules"
							v-model.number="shipment.totalBox"
							label="Number of Boxes"
							dense
							rounded
							outlined></v-text-field>

						<v-text-field
							:rules="numberRules"
							v-model.number="shipment.totalWeight"
							label="Total Weight"
							dense
							rounded
							outlined
							suffix="Lbs"></v-text-field>

						<v-text-field
							:rules="numberRules"
							v-model.number="shipment.totalValue"
							prefix="$"
							label="Total Declared Value"
							dense
							rounded
							outlined></v-text-field>
					</v-container>
				</v-card-text>
				<v-card-text class="py-0 mb-2" v-else>
					<v-container
						class="py-0 my-0 d-flex justify-space-between">
						<v-container>
							<v-text-field
								:rules="numberRules"
								v-model.number="shipment.totalBox"
								label="Number of Boxes"
								dense
								rounded
								outlined></v-text-field>
						</v-container>
						<v-container>
							<v-text-field
								:rules="numberRules"
								v-model.number="shipment.totalPallet"
								label="Number of Pallets"
								dense
								rounded
								outlined></v-text-field>
						</v-container>
						<v-container>
							<v-text-field
								:rules="numberRules"
								v-model.number="shipment.totalValue"
								prefix="$"
								label="Total Declared Value"
								dense
								rounded
								outlined></v-text-field>
						</v-container>
					</v-container>
				</v-card-text>
				<v-card-text class="py-0 mb-2">
					<v-container class="py-0 my-0">
						<v-container>
							<v-textarea
								rounded
								outlined
								v-model="shipment.description"
								label="Description"></v-textarea>
						</v-container>
					</v-container>
				</v-card-text>
			</v-card>
			<v-card elevation="0" min-width="40%" class="rounded-xl">
				<v-card-text>
					<v-container class="pa-4">
						<div
							class="d-flex align-center justify-space-between mb-4">
							<div class="form-section-title">
								Parcel List
							</div>
							<v-chip
								v-if="parcels.length > 0"
								color="primary"
								outlined
								small>
								{{
									parcels.filter((p) => p.isActive !== false)
										.length
								}}
								{{
									parcels.filter((p) => p.isActive !== false)
										.length === 1
										? "Parcel"
										: "Parcels"
								}}
							</v-chip>
						</div>

						<v-divider class="mb-4"></v-divider>

						<div
							v-if="parcels.length === 0"
							class="text-center py-8">
							<v-icon
								size="64"
								color="grey lighten-1"
								class="mb-3">
								mdi-package-variant
							</v-icon>
							<p class="grey--text text--darken-1 mb-1 text-h6">
								No parcels added yet
							</p>
							<p class="grey--text text-caption">
								Click "Add Parcel" to get started
							</p>
						</div>

						<v-card
							v-for="(parcel, idx) in parcels"
							:key="`parcel-${idx}`"
							class="mb-3 pa-3 rounded-xl"
							:class="{
								'parcel-inactive': parcel.isActive === false,
							}"
							outlined
							elevation="0">
							<v-row dense>
								<v-col cols="12" sm="6">
									<v-text-field
										:rules="numberRules"
										label="Weight"
										dense
										outlined
										hide-details="auto"
										suffix="Lbs"
										type="number"
										:value="parcel.weight"
										@input="
											parcel.weight = $event
												? parseFloat($event)
												: 0
										"
										:disabled="parcel.isActive === false"
										class="rounded-xl"></v-text-field>
								</v-col>
								<v-col cols="12" sm="6">
									<v-text-field
										:rules="numberRules"
										prefix="$"
										label="Declared Value"
										dense
										outlined
										hide-details="auto"
										type="number"
										:value="parcel.value"
										@input="
											parcel.value = $event
												? parseFloat($event)
												: 0
										"
										:disabled="parcel.isActive === false"
										class="rounded-xl">
										<template
											v-slot:prepend-inner></template>
									</v-text-field>
								</v-col>
							</v-row>

							<v-divider class="my-2"></v-divider>

							<div class="d-flex justify-end">
								<v-btn
									v-if="!parcel.id"
									x-small
									color="error"
									text
									@click="deleteParcel(idx)">
									<v-icon x-small left>mdi-delete</v-icon>
									Delete
								</v-btn>
								<v-btn
									v-if="
										parcel.id &&
										parcel.isActive !== false &&
										isUpdating
									"
									x-small
									color="error"
									text
									@click="removeParcel(idx)">
									<v-icon x-small left>mdi-delete</v-icon>
									Remove
								</v-btn>
								<v-btn
									v-if="
										parcel.id &&
										parcel.isActive === false &&
										isUpdating
									"
									x-small
									color="success"
									text
									@click="undoRemoveParcel(idx)">
									<v-icon x-small left>mdi-undo</v-icon>
									Restore
								</v-btn>
							</div>
						</v-card>

						<v-btn
							color="primary"
							depressed
							block
							rounded
							class="mt-2"
							@click="addParcel">
							<v-icon small left>mdi-plus</v-icon>
							Add Parcel
						</v-btn>
					</v-container>
				</v-card-text>
			</v-card>
			<customer-dialog v-model="customerDialog"></customer-dialog>
			<general-dialog
				:dialog="deleteDialog"
				v-model="deleteDialog"
				:options="deleteOptions"
				@handle-event="deleteShipment"></general-dialog>
			<general-dialog
				:dialog="updateDialog"
				v-model="updateDialog"
				:options="updateOptions"
				@handle-event="updateInvoice"></general-dialog>
		</v-container>
	</v-form>
</template>
<script>
import eventBus from "@/js/event-bus";
import backendService from "@/services/backend-service";
import generalMixin from "@/js/general-mixin.js";
import {
	InvoiceType,
	ShipmentStatus,
	CurrencyType,
	ShipmentType,
} from "@/js/enum.js";
import GeneralDialog from "@/components/GeneralDialog.vue";
import CustomerDialog from "@/components/CustomerDialog.vue";
import Decimal from "decimal.js";
export default {
	name: "ShipmentForm",
	mixins: [generalMixin],
	components: {
		"customer-dialog": CustomerDialog,
		"general-dialog": GeneralDialog,
	},
	data() {
		return {
			formValid: false,
			originalShipment: {},
			originalParcels: [],
			isUpdating: false,
			isGenerateLoading: false,
			isLoading: false,
			parcels: [],
			shipment: {
				mtlTracking: null,
				description: "",
				totalBox: 0,
				totalPallet: 0,
				totalWeight: 0,
				totalValue: 100,
				shipmentStatusId: this.getIdByEnum(ShipmentStatus.New),
				typeId: null,
			},
			deleteDialog: false,
			isDeleteLoading: false,
			deleteOptions: {
				btnText: "Yes",
				message: "This action is not revertable!",
				title: "Are you sure you want to delete this shipment?",
			},
			updateDialog: false,
			isUpdateLoading: false,
			updateOptions: {
				btnText: "Yes",
				message: "This action is not revertable!",
				title: "Are you sure you want to update the invoice?",
			},
			numberRules: [
				(v) => {
					if (v) return true;
					return "Required";
				},
				(v) => {
					if (!isNaN(v)) return true;
					return "Must be numeric";
				},
				(v) => {
					if (v > 0) return true;
					return "Must be greater than 0";
				},
			],
			customerDialog: false,
			customer: {},
		};
	},
	mounted: function () {
		if (this.$route.params.id) {
			this.getUpdateForm();
		}
		this.$refs.form.validate();
	},
	computed: {
		customers() {
			return this.$store.getters["customers"];
		},
		isAirShipment() {
			if (this.shipment.typeId) {
				return (
					this.shipment.typeId == this.getIdByEnum(ShipmentType.Air)
				);
			}
			return true;
		},
		shipmentGroups() {
			return this.$store.getters["shipmentGroups"];
		},
		shipments: {
			get() {
				return this.$store.getters["shipments"];
			},
			set(val) {
				this.$store.commit("setShipments", val);
			},
		},
		paymentStatuses() {
			if (
				this.$store.getters["dropdownToOptions"] &&
				this.$store.getters["dropdownToOptions"]["paymentStatus"]
			)
				return this.$store.getters["dropdownToOptions"]["paymentStatus"]
					.options;
			return [];
		},
		shipmentStatuses() {
			if (
				this.$store.getters["dropdownToOptions"] &&
				this.$store.getters["dropdownToOptions"]["shipmentStatus"]
			)
				return this.$store.getters["dropdownToOptions"][
					"shipmentStatus"
				].options;
			return [];
		},
	},
	methods: {
		addParcel() {
			this.parcels.push({ isActive: true, weight: 0, value: 0 });
		},
		addShipment() {
			if (!this.$refs.form.validate()) return;
			this.isLoading = true;
			this.shipment.parcels = this.parcels;
			backendService
				.addShipment(this.shipment)
				.then((response) => {
					this.shipment = response.data;
					this.parcels = this.shipment.parcels;
					this.$store.commit("addShipment", this.shipment);
					this.timer(1000).then(() => {
						this.isLoading = false;
						this.isUpdating = true;
						this.$router.push({
							name: "ShipmentUpdate",
							params: { id: this.shipment.id },
						});
						this.successSnackbar(
							"Successfully added a new shipment"
						);
					});
				})
				.catch((err) => {
					let message = err.response.data;
					this.timer(1000).then(() => {
						if (
							typeof message == "string" &&
							message.includes("Duplicate")
						) {
							this.errorSnackbar(
								"Repeated tracking # under shipment list"
							);
						} else {
							this.errorSnackbar("Ops! Something is not right");
						}
						this.isLoading = false;
					});
				});
		},
		deleteShipment() {
			this.isDeleteLoading = true;
			backendService
				.deleteShipmentById(this.shipment.id)
				.then(() => {
					setTimeout(() => {
						this.isDeleteLoading = false;
						this.$router.push({ name: "ShipmentList" });
						eventBus.$emit(
							"setSnackbar",
							"Successfully delete the shipment",
							"success",
							true
						);
					}, 1000);
				})
				.catch(() => {
					this.isDeleteLoading = false;
					eventBus.$emit(
						"setSnackbar",
						"Ops! Something is not right!",
						"error",
						true
					);
				}, 1000);
		},
		deleteParcel(idx) {
			this.parcels.splice(idx, 1);
		},
		formatShipmentGroup(shipmentGroup) {
			if (shipmentGroup) {
				return `${shipmentGroup.id} - ${this.convertToDate(
					shipmentGroup.shipmentDate,
					false
				)} - ${this.getOptionName(
					shipmentGroup.statusId
				)} - ${this.getOptionName(shipmentGroup.typeId)}`;
			}
		},
		generateInvoice() {
			this.isGenerateLoading = true;
			this.timer(1000).then(() => {
				this.$router.push({
					name: "NewInvoice",
					params: {
						shipment: this.shipment,
						isGenerating: true,
						type: this.getIdByEnum(InvoiceType.Shipment),
					},
				});
			});
		},
		getCustomerName(item) {
			return `${item.id} - ${item.name} - ${item.phone}`;
		},
		getUpdateForm() {
			this.isUpdating = true;
			this.getShipmentById(this.$route.params.id);
		},
		getShipmentById(id) {
			backendService.getShipmentById(id).then((response) => {
				this.shipment = response.data;
				this.parcels = this.shipment.parcels;
				this.originalShipment = JSON.stringify(this.shipment);
			});
		},
		handleGroupChange(id) {
			let shipmentGroup = this.shipmentGroups.find((e) => e.id == id);
			if (shipmentGroup) {
				this.shipment.typeId = shipmentGroup.typeId;
				if (
					this.getIdByEnum(ShipmentType.Air) == shipmentGroup.typeId
				) {
					this.$set(this.shipment, "totalPallet", 0);
				} else {
					this.$set(this.shipment, "totalWeight", 0);
				}
			}
		},
		undoRemoveParcel(idx) {
			this.parcels[idx].isActive = true;
		},
		removeParcel(idx) {
			this.parcels[idx].isActive = false;
		},
		updateInvoice() {
			let invoice = {};
			let invoiceDetails = [];
			invoice.invoiceDetails = invoiceDetails;
			invoice.id = this.shipment.invoiceId;
			invoice.customerId = this.shipment.senderId;
			invoice.typeId = this.getIdByEnum(InvoiceType.Shipment);
			invoice.currencyId = this.getIdByEnum(CurrencyType.UsDollar);
			invoice.associatedId = this.shipment.id;
			invoice.mtlTracking = this.shipment.mtlTracking;
			invoice.description = this.shipment.description;
			invoice.totalAmount = Decimal.mul(this.shipment.totalWeight, 2.75);
			let invoiceDetail = {
				name: `Shipment ${this.shipment.id}`,
				quantity: this.shipment.totalWeight,
				unitPrice: 2.75,
				isActive: true,
			};
			invoiceDetails.push(invoiceDetail);

			backendService
				.updateInvoice(invoice)
				.then(() => {
					this.timer(1000).then(() => {
						this.isUpdateLoading = false;
						this.successSnackbar(
							"Successfully updated the invoice"
						);
					});
				})
				.catch(() => {
					this.errorSnackbar("Ops! Something is not right");
				});
		},
		updateShipment() {
			if (!this.$refs.form.validate()) return;
			this.isLoading = true;
			this.shipment.parcels = this.parcels;
			backendService
				.updateShipment(this.shipment)
				.then((response) => {
					this.$store.commit("updateShipment", this.shipment);
					this.timer(1000).then(() => {
						this.shipment = response.data;
						this.parcels = this.shipment.parcels;
						this.originalShipment = JSON.stringify(this.shipment);
						this.isLoading = false;
						this.successSnackbar(
							"Successfully updated a new shipment"
						);
					});
				})
				.catch((err) => {
					let message = err.response.data;
					this.timer(1000).then(() => {
						if (
							typeof message == "string" &&
							message.includes("Duplicate")
						) {
							this.errorSnackbar(
								"Repeated tracking # under shipment list"
							);
						} else {
							this.errorSnackbar("Ops! Something is not right");
						}
						this.isLoading = false;
					});
				});
		},
		updateSenderInfo() {
			let customer = this.customers.find(
				(x) => x.id == this.shipment.senderId
			);
			this.shipment.senderAddress1 = customer.address1;
			this.shipment.senderAddress2 = customer.address2;
			this.shipment.senderPhone = customer.phone;
			this.shipment.senderEmail = customer.email;
		},
		updateReceiverInfo() {
			let customer = this.customers.find(
				(x) => x.id == this.shipment.receiverId
			);
			this.shipment.receiverAddress1 = customer.address1;
			this.shipment.receiverAddress2 = customer.address2;
			this.shipment.receiverPhone = customer.phone;
			this.shipment.receiverEmail = customer.email;
		},
	},
	watch: {
		"$route.name": {
			handler: function (name) {
				if (name == "NewShipment") {
					this.isLoading = false;
					this.isUpdating = false;
					this.parcels = [];
					this.shipment = {
						mtlTracking: "",
						description: "",
						totalBox: 0,
						totalPallet: 0,
						totalWeight: 0,
						totalValue: 100,
						shipmentStatusId: this.getIdByEnum(ShipmentStatus.New),
						typeId: null,
					};
				}
			},
			deep: true,
			immediate: true,
		},
	},
};
</script>

<style scoped>
.shipment-form-page {
	gap: 8px;
}
.shipment-form-page :deep(.v-card__title) {
	padding: 16px 16px 12px;
}
.shipment-form-page :deep(.v-card__text) {
	margin-bottom: 0 !important;
	padding: 0 16px 8px !important;
}
.shipment-form-page :deep(.v-card__text > .container:not(.pa-4)),
.shipment-form-page :deep(.v-card__text > .container-fluid:not(.pa-4)) {
	max-width: none;
	padding: 0 !important;
}
.shipment-form-page :deep(.v-card__text > .container.d-flex:not(.pa-4)),
.shipment-form-page :deep(.v-card__text > .container-fluid.d-flex:not(.pa-4)) {
	gap: 8px;
}
.shipment-form-page :deep(.v-card__text > .container > .container),
.shipment-form-page :deep(.v-card__text > .container-fluid > .container) {
	padding-left: 0;
	padding-right: 0;
}
.parcel-inactive {
	opacity: 0.6;
	background-color: #fafafa !important;
}

.parcel-inactive .v-input--is-disabled {
	color: rgba(0, 0, 0, 0.38);
}
</style>
